import type { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";

import { ApiError } from "@/app/common-types/api-error.types";
import { RenewalPage } from "@/config/app-constant";
import fetchGenericError from "@/hooks/magnolia/fetch-generic-errors";
import {
  clearApiError,
  setApiError,
} from "@/store/slices/errors/api-error.slice";
import { clearQuote } from "@/store/slices/quote/quote.slice";
import { apiErrorMappings } from "@/utils/errors/api-error-map";

import errorHandlers from "./error-handlers";

export interface ErrorResponse {
  code?: number;
  errors?: {
    code: number;
    message: string;
  }[];
}

const handleError = async (
  error: ApiError<ErrorResponse>,
  router: ReturnType<typeof useRouter>,
  dispatch: ReturnType<typeof useDispatch>,
  handleOnClick?: () => void
) => {
  const errorResponse = error.response;
  const errorCode = errorResponse?.data?.errors?.[0]?.code;
  const applicationName = process.env.NEXT_PUBLIC_APPLICATION ?? "quote";

  const redirectToHome = () => {
    dispatch(clearQuote());
    if (typeof window !== "undefined") {
      const { search } = window.location;
      router.push(search ? `/${search}` : "/");
    } else {
      router.push("/");
    }
  };

  const handleDefaultError = async () => {
    const errorObject = await apiErrorMappings(error);
    errorObject.multiButton = true;
    dispatch(setApiError(errorObject));
  };

  const handleGenericError = async (errorStatusType: string) => {
    const errorObjectValue = await apiErrorMappings(error);
    errorObjectValue.multiButton = false;
    fetchGenericError(errorStatusType).then(async (data) => {
      const errorObject = await apiErrorMappings(error);
      errorObject.ctaPrimaryBtnLabel = data?.ctaPrimaryBtnLabel ?? "Go back";
      errorObject.modalTitle = data?.title ?? "Something <br> went wrong";
      errorObject.modalDescription =
        data?.modalDescription ??
        "Please try again. If the problem persists, please contact us.";
      errorObject.onPrimaryCtaClick =
        errorStatusType === "4XX"
          ? ({ handleModalClose }) => {
              router.back();
              dispatch(clearApiError());
              handleModalClose(false);
            }
          : () => router.push(process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!);
      dispatch(setApiError(errorObject));
    });
  };

  const is4XX =
    errorResponse?.status &&
    /^4\d{2}$/.test(errorResponse?.status as unknown as string);
  const is5XX =
    errorResponse?.status &&
    /^5\d{2}$/.test(errorResponse?.status as unknown as string);

  if (
    errorResponse?.status === 401 &&
    applicationName === RenewalPage &&
    errorCode === 4001
  ) {
    await errorHandlers[errorCode as number](
      error,
      dispatch,
      router,
      handleOnClick
    );
  } else if ((is4XX || is5XX) && applicationName === RenewalPage) {
    await handleGenericError(is5XX ? "5XX" : "4XX");
  } else if (errorResponse?.status === 401 || errorResponse?.status === 410) {
    redirectToHome();
  } else if (
    errorResponse?.status === 400 &&
    (errorCode === 4029 || errorCode === 1003)
  ) {
    redirectToHome();
  } else if (errorResponse?.status === 400) {
    await (errorHandlers[errorCode as number] || handleDefaultError)(
      error,
      dispatch,
      router,
      handleOnClick,
      errorCode
    );
  } else if (errorResponse?.status === 403 && errorCode === 4107) {
    redirectToHome();
  } else {
    await (errorHandlers[errorCode as number] || handleDefaultError)(
      error,
      dispatch,
      router,
      handleOnClick
    );
  }
};

export default handleError;
